import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { format, parse } from 'date-fns';
import useCustomFetch from '../../hooks/useCustomFetch';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { ContentCopy } from '@mui/icons-material';

function ChurchSettings() {
  const { t } = useTranslation();
  const { user, canEdit } = useAuth();
  const { get, put } = useCustomFetch();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    contact: {
      email: '',
      phone: '',
      website: ''
    },
    settings: {
      defaultServiceTime: new Date(),
      timezone: '',
      language: ''
    },
    confirmationCode: ''
  });

  useEffect(() => {
    fetchChurchData();
  }, []);

  const fetchChurchData = async () => {
    try {
      setLoading(true);
      const response = await get('churches/me');
      console.log('Church data response:', response);
      
      if (response?.data) {
        const churchData = response.data;
        setFormData({
          name: churchData.name || '',
          address: {
            street: churchData.address?.street || '',
            city: churchData.address?.city || '',
            state: churchData.address?.state || '',
            zipCode: churchData.address?.zipCode || '',
            country: churchData.address?.country || ''
          },
          contact: {
            email: churchData.contact?.email || '',
            phone: churchData.contact?.phone || '',
            website: churchData.contact?.website || ''
          },
          settings: {
            defaultServiceTime: churchData.settings?.defaultServiceTime 
              ? parse(churchData.settings.defaultServiceTime, 'HH:mm', new Date())
              : new Date(),
            timezone: churchData.settings?.timezone || '',
            language: churchData.settings?.language || ''
          },
          confirmationCode: churchData.confirmationCode || ''
        });
      }
    } catch (err) {
      console.error('Error fetching church data:', err);
      setError(t('errors.failedToFetchChurch', 'Failed to fetch church data'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleTimeChange = (time) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        defaultServiceTime: time
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      const updatedData = {
        ...formData,
        settings: {
          ...formData.settings,
          defaultServiceTime: formData.settings.defaultServiceTime 
            ? format(formData.settings.defaultServiceTime, 'HH:mm')
            : null,
          generateCode: true
        }
      };

      const response = await put('churches/me', updatedData);
      
      if (response?.success) {
        setSuccess(true);
        await fetchChurchData();
      }
    } catch (err) {
      console.error('Error updating church:', err);
      setError(t('settings.church.updateError', 'Failed to update church settings'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(formData.confirmationCode);
    setSuccess(t('settings.church.confirmationCode.copied'));
    setTimeout(() => setSuccess(false), 3000);
  };

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Sao_Paulo',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo'
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'es', name: 'Spanish' }
  ];

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          {user.role === 'admin' && (
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'primary.light' }}>
              <Typography variant="h6" color="primary.contrastText" gutterBottom>
                {t('settings.church.confirmationCode.title')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" color="primary.contrastText">
                  {t('settings.church.confirmationCode.shareText')}: <strong>{formData.confirmationCode}</strong>
                </Typography>
                <Tooltip title={t('settings.church.confirmationCode.copyTooltip')}>
                  <IconButton onClick={handleCopyCode} size="small" sx={{ color: 'primary.contrastText' }}>
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="primary.contrastText">
                {t('settings.church.confirmationCode.helperText')}
              </Typography>
            </Paper>
          )}

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('settings.church.basicInfo.title')}
              </Typography>
              <TextField
                required
                fullWidth
                name="name"
                label={t('settings.church.basicInfo.name')}
                placeholder={t('settings.church.basicInfo.namePlaceholder')}
                value={formData.name}
                onChange={handleInputChange}
                disabled={!canEdit()}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('settings.church.address.title')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="address.street"
                    label={t('settings.church.address.street')}
                    value={formData.address.street}
                    onChange={handleInputChange}
                    disabled={!canEdit()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="address.city"
                    label={t('settings.church.address.city')}
                    value={formData.address.city}
                    onChange={handleInputChange}
                    disabled={!canEdit()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="address.state"
                    label={t('settings.church.address.state')}
                    value={formData.address.state}
                    onChange={handleInputChange}
                    disabled={!canEdit()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="address.zipCode"
                    label={t('settings.church.address.zipCode')}
                    value={formData.address.zipCode}
                    onChange={handleInputChange}
                    disabled={!canEdit()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="address.country"
                    label={t('settings.church.address.country')}
                    value={formData.address.country}
                    onChange={handleInputChange}
                    disabled={!canEdit()}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('settings.church.contact.title')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="contact.email"
                    label={t('settings.church.contact.email')}
                    value={formData.contact.email}
                    onChange={handleInputChange}
                    disabled={!canEdit()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="contact.phone"
                    label={t('settings.church.contact.phone')}
                    value={formData.contact.phone}
                    onChange={handleInputChange}
                    disabled={!canEdit()}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="contact.website"
                    label={t('settings.church.contact.website')}
                    value={formData.contact.website}
                    onChange={handleInputChange}
                    disabled={!canEdit()}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Church Preferences */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('settings.church.preferences.title')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      label={t('settings.church.preferences.defaultServiceTime')}
                      value={formData.settings.defaultServiceTime}
                      onChange={handleTimeChange}
                      disabled={!canEdit()}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('settings.church.preferences.timezone')}</InputLabel>
                    <Select
                      name="settings.timezone"
                      value={formData.settings.timezone}
                      onChange={handleInputChange}
                      disabled={!canEdit()}
                      label={t('settings.church.preferences.timezone')}
                    >
                      {timezones.map((tz) => (
                        <MenuItem key={tz} value={tz}>
                          {tz}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('settings.church.preferences.language')}</InputLabel>
                    <Select
                      name="settings.language"
                      value={formData.settings.language}
                      onChange={handleInputChange}
                      disabled={!canEdit()}
                      label={t('settings.church.preferences.language')}
                    >
                      {languages.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {canEdit() && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {t('settings.church.save')}
              </Button>
            </Box>
          )}
        </form>
      )}
    </Box>
  );
}

export default ChurchSettings;
